import React, { useMemo } from "react";
import { conditionTypes } from "../../../../Findings/FindingsFilters/FilterRow/FilterRowUtils";
import { Tooltip, RadioButtonsList } from "@SeemplicityDev/shared";

export interface ConditionOption {
  label?: string;
  longLabel?: string;
  value: string;
  type?: string;
  deprecated?: boolean;
}

interface EnrichedConditionOption extends ConditionOption {
  disabled: boolean;
  label: string;
  name: string;
}

export interface FilterConditionSelectProps {
  isEditable?: boolean;
  conditionOptions?: ConditionOption[];
  onChange: (value: string, shouldResetValue: boolean) => void;
  currentValue?: string;
  isActive?: boolean;
  onClick: () => void;
}

const FilterConditionSelect: React.FC<FilterConditionSelectProps> = ({
  isEditable = true,
  conditionOptions = [],
  onChange,
  currentValue,
  isActive,
  onClick,
}) => {
  const currentConditionData = useMemo(
    () => conditionOptions.find((option) => option.value === currentValue),
    [currentValue, conditionOptions]
  );

  const enrichedConditionOptions = useMemo<EnrichedConditionOption[]>(() => {
    return conditionOptions.map((conditionOption) => {
      return {
        ...conditionOption,
        disabled: !!conditionOption.deprecated,
        label: conditionOption.longLabel || conditionOption.label || "",
        name: conditionOption.value,
      };
    });
  }, [conditionOptions]);

  return (
    <div className={"filter-condition-selector-wrapper"}>
      <Tooltip content={currentConditionData?.label}>
        <div
          data-testid={"filter-condition-chip"}
          className={`condition-selected-value ${!isEditable ? "is-disabled" : ""} ${isActive ? "is-active" : ""}`}
          onClick={() => {
            if (isEditable) {
              onClick();
            }
          }}
        >
          {currentConditionData?.deprecated && (
            <i className={`seem-icon seem-icon-warning deprecated-condition`} />
          )}
          {currentConditionData?.label}
        </div>
      </Tooltip>
      {isActive && (
        <div className={"filter-condition-component-wrapper"}>
          <RadioButtonsList
            noMargin
            value={currentValue}
            onChange={(val: string) => {
              if (!!(val as unknown as ConditionOption)?.deprecated) {
                return;
              }
              // conditionTypes is an untyped JS map; index it as a string record
              // so a dynamic `val` can look up a (possibly missing) type.
              const conditionTypesMap = conditionTypes as Record<
                string,
                string | undefined
              >;
              // prevent a case of not resetting value for unsupported types! The check should happen only if there are types.
              //if value not contained in "conditionTypes", value should be reset
              const shouldResetValue =
                !conditionTypesMap[val] ||
                (!!conditionTypesMap[val] &&
                  !!currentValue &&
                  conditionTypesMap?.[val] !== currentConditionData?.type);
              onChange(val, shouldResetValue);
            }}
            options={enrichedConditionOptions}
            direction={"row"}
          />
        </div>
      )}
    </div>
  );
};

export default FilterConditionSelect;
